import { useState } from 'react';
import { useBatchStore } from '@/store/useBatchStore';
import { XCircle, CheckCircle2, X, Minimize2, Maximize2 } from 'lucide-react';
import { cn } from '@/lib/utils';

function getPercent(current: number, total: number) {
    if (total <= 0) return 0;
    return Math.min(100, Math.max(0, Math.round((current / total) * 100)));
}

function formatElapsed(elapsedMs: number) {
    const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const time = [minutes, seconds].map(value => String(value).padStart(2, '0')).join(':');
    return hours > 0 ? `${String(hours).padStart(2, '0')}:${time}` : time;
}

// 全局批量操作进度 - 固定在右下角，跨页面持久显示
export function BatchProgressBar() {
    const { tasks, cancelTask, dismissTask } = useBatchStore();
    const [collapsed, setCollapsed] = useState(false);
    const taskList = Array.from(tasks.values());

    if (taskList.length === 0) return null;

    if (collapsed) {
        return (
            <div className="fixed bottom-4 right-4 z-50 flex max-w-[calc(100vw-2rem)] items-center gap-2 rounded-lg border border-[#cfe3e2] bg-white px-3 py-2 shadow-lg">
                <div className="min-w-0 space-y-0.5">
                    {taskList.map(task => {
                        const percent = getPercent(task.progress.current, task.progress.total);
                        return (
                            <div key={task.id} className="flex min-w-0 items-center gap-2 text-xs">
                                {task.running ? (
                                    <span className="h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-[#159b96] border-t-transparent" />
                                ) : (
                                    <CheckCircle2 size={13} className="shrink-0 text-green-500" />
                                )}
                                <span className="max-w-32 truncate font-medium text-zinc-700">{task.label}</span>
                                <span className="shrink-0 font-semibold tabular-nums text-[#159b96]">{percent}%</span>
                                {task.progress.elapsedMs !== undefined && (
                                    <span className="shrink-0 tabular-nums text-zinc-400">{formatElapsed(task.progress.elapsedMs)}</span>
                                )}
                            </div>
                        );
                    })}
                </div>
                <button
                    type="button"
                    onClick={() => setCollapsed(false)}
                    className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-zinc-400 hover:bg-[#effafa] hover:text-[#159b96]"
                    title="展开进度"
                    aria-label="展开进度"
                >
                    <Maximize2 size={14} />
                </button>
            </div>
        );
    }

    return (
        <div className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)] overflow-hidden rounded-lg border border-[#cfe3e2] bg-white shadow-xl">
            <div className="flex h-10 items-center justify-between border-b border-zinc-100 px-3">
                <span className="text-xs font-semibold text-zinc-700">处理进度</span>
                <button
                    type="button"
                    onClick={() => setCollapsed(true)}
                    className="flex h-7 w-7 items-center justify-center rounded-md text-zinc-400 hover:bg-[#effafa] hover:text-[#159b96]"
                    title="缩小进度"
                    aria-label="缩小进度"
                >
                    <Minimize2 size={14} />
                </button>
            </div>
            <div className="max-h-64 space-y-2 overflow-y-auto p-3">
                {taskList.map(task => {
                    const percent = getPercent(task.progress.current, task.progress.total);
                    return (
                        <div key={task.id} className="rounded-md border border-zinc-100 bg-zinc-50/70 p-2.5">
                            <div className="flex items-center gap-2">
                                {task.running ? (
                                    <span className="h-3.5 w-3.5 shrink-0 animate-spin rounded-full border-2 border-[#159b96] border-t-transparent" />
                                ) : (
                                    <CheckCircle2 size={15} className="shrink-0 text-green-500" />
                                )}
                                <span className="min-w-0 flex-1 truncate text-xs font-medium text-zinc-700">{task.label}</span>
                                <span className="shrink-0 text-xs text-zinc-400">{task.progress.current}/{task.progress.total}</span>
                                <span className="w-10 shrink-0 text-right text-xs font-semibold tabular-nums text-[#159b96]">{percent}%</span>
                                {task.running ? (
                                    <button type="button" onClick={() => cancelTask(task.id)} className="shrink-0 rounded p-0.5 text-red-400 transition-colors hover:bg-red-50 hover:text-red-600" title="取消" aria-label={`取消${task.label}`}>
                                        <XCircle size={14} />
                                    </button>
                                ) : (
                                    <button type="button" onClick={() => dismissTask(task.id)} className="shrink-0 rounded p-0.5 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600" title="关闭" aria-label={`关闭${task.label}`}>
                                        <X size={14} />
                                    </button>
                                )}
                            </div>
                            {task.progress.elapsedMs !== undefined && (
                                <p className="mt-1.5 text-[11px] tabular-nums text-[#159b96]">已用时 {formatElapsed(task.progress.elapsedMs)}</p>
                            )}
                            <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-zinc-200">
                                <div
                                    className={cn('h-full rounded-full transition-all duration-300', task.running ? 'bg-[#159b96]' : 'bg-green-500')}
                                    style={{ width: `${percent}%` }}
                                />
                            </div>
                            <p className="mt-1.5 truncate text-[11px] text-zinc-500" title={task.progress.message}>{task.progress.message}</p>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
